import React, { useEffect } from 'react';
import { APP_NAME } from '../../support/brand';
import { COMPANY_NAME, COMPANY_CNPJ, COMPANY_EMAIL } from '../../support/legal';
import LegalPageLayout, { LegalSection } from '../../components/legal/LegalPageLayout';

function PrivacyPolicy() {
  useEffect(() => {
    document.title = `Política de Privacidade — ${APP_NAME}`;
  }, []);

  return (
    <LegalPageLayout title="Política de Privacidade">
      <LegalSection title="1. Controlador dos dados">
        <p>
          A {COMPANY_NAME}, inscrita no CNPJ {COMPANY_CNPJ}, é a controladora dos dados pessoais
          tratados por meio da plataforma {APP_NAME}, disponível em okmaia.com.
        </p>
        <p>
          Para questões relacionadas a privacidade e proteção de dados, entre em contato pelo
          e-mail{' '}
          <a href={`mailto:${COMPANY_EMAIL}`} className="text-forge-600 hover:underline">
            {COMPANY_EMAIL}
          </a>
          .
        </p>
      </LegalSection>

      <LegalSection title="2. Escopo">
        <p>
          Esta Política de Privacidade descreve como coletamos, usamos, armazenamos e protegemos
          dados pessoais de usuários da plataforma {APP_NAME}, em conformidade com a Lei Geral de
          Proteção de Dados (Lei nº 13.709/2018 — LGPD).
        </p>
      </LegalSection>

      <LegalSection title="3. Dados coletados">
        <p>Podemos coletar e tratar as seguintes categorias de dados:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <strong>Dados de cadastro:</strong> nome, e-mail, senha (armazenada de forma
            criptografada) e nome do workspace.
          </li>
          <li>
            <strong>Dados de uso da plataforma:</strong> configurações de agentes e sub-agentes,
            bases de conhecimento, prompts, instruções, logs de conversas e interações com a IA.
          </li>
          <li>
            <strong>Dados de integrações:</strong> informações de contatos em canais como WhatsApp
            (telefone, mensagens trocadas), configurações de roteadores de entrada e mensagens
            agendadas.
          </li>
          <li>
            <strong>Dados técnicos:</strong> endereço IP, tipo de navegador, registros de acesso,
            cookies de sessão e token de autenticação (JWT) armazenado localmente.
          </li>
        </ul>
      </LegalSection>

      <LegalSection title="4. Finalidades do tratamento">
        <p>Utilizamos os dados pessoais para as seguintes finalidades:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>Prestação e operação da plataforma {APP_NAME}.</li>
          <li>Criação e gestão de contas e workspaces.</li>
          <li>Processamento de conversas e geração de respostas por agentes de IA.</li>
          <li>Integração com canais de comunicação, como WhatsApp.</li>
          <li>Suporte técnico e atendimento ao usuário.</li>
          <li>Segurança, prevenção de fraudes e auditoria de acesso.</li>
          <li>Melhoria contínua do produto e da experiência do usuário.</li>
        </ul>
      </LegalSection>

      <LegalSection title="5. Bases legais">
        <p>O tratamento de dados pessoais é realizado com base nas seguintes hipóteses da LGPD:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <strong>Execução de contrato:</strong> para fornecer os serviços contratados pelo
            usuário.
          </li>
          <li>
            <strong>Legítimo interesse:</strong> para segurança, melhoria do serviço e prevenção de
            fraudes.
          </li>
          <li>
            <strong>Consentimento:</strong> quando aplicável, especialmente para integrações com
            terceiros ou funcionalidades opcionais.
          </li>
          <li>
            <strong>Cumprimento de obrigação legal:</strong> quando exigido por lei ou autoridade
            competente.
          </li>
        </ul>
      </LegalSection>

      <LegalSection title="6. Compartilhamento com terceiros">
        <p>
          Para operar a plataforma, podemos compartilhar dados com prestadores de serviço, sempre
          mediante contratos que garantam a proteção dos dados:
        </p>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <strong>Provedores de IA:</strong> OpenAI, Anthropic e similares, para processamento de
            linguagem natural e geração de respostas.
          </li>
          <li>
            <strong>Infraestrutura:</strong> AWS, Heroku, PostgreSQL, Redis e OpenSearch, para
            hospedagem, armazenamento e busca de dados.
          </li>
          <li>
            <strong>Canais de comunicação:</strong> WhatsApp/Meta, para envio e recebimento de
            mensagens.
          </li>
        </ul>
        <p>
          Não vendemos dados pessoais. O compartilhamento ocorre apenas na medida necessária para
          a operação do serviço.
        </p>
      </LegalSection>

      <LegalSection title="7. Retenção e exclusão">
        <p>
          Mantemos os dados pessoais pelo tempo necessário para cumprir as finalidades descritas
          nesta política, respeitando prazos legais e contratuais. Após o encerramento da conta,
          os dados podem ser excluídos ou anonimizados, salvo quando a retenção for exigida por lei
          ou para exercício de direitos em processos judiciais ou administrativos.
        </p>
      </LegalSection>

      <LegalSection title="8. Direitos do titular">
        <p>
          Nos termos da LGPD, você tem direito a solicitar, mediante requisição ao controlador:
        </p>
        <ul className="list-disc pl-5 space-y-2">
          <li>Confirmação da existência de tratamento e acesso aos dados.</li>
          <li>Correção de dados incompletos, inexatos ou desatualizados.</li>
          <li>Anonimização, bloqueio ou eliminação de dados desnecessários ou excessivos.</li>
          <li>Portabilidade dos dados a outro fornecedor de serviço.</li>
          <li>Eliminação dos dados tratados com base no consentimento.</li>
          <li>Informação sobre compartilhamento com terceiros.</li>
          <li>Revogação do consentimento, quando aplicável.</li>
        </ul>
        <p>
          Para exercer seus direitos, envie um e-mail para{' '}
          <a href={`mailto:${COMPANY_EMAIL}`} className="text-forge-600 hover:underline">
            {COMPANY_EMAIL}
          </a>
          .
        </p>
      </LegalSection>

      <LegalSection title="9. Segurança">
        <p>
          Adotamos medidas técnicas e organizacionais para proteger os dados pessoais, incluindo
          criptografia de senhas, comunicação via HTTPS, controle de acesso por workspace e
          monitoramento de acessos. Nenhum sistema é completamente imune a riscos, mas trabalhamos
          continuamente para reduzir vulnerabilidades.
        </p>
      </LegalSection>

      <LegalSection title="10. Cookies e armazenamento local">
        <p>
          Utilizamos cookies e armazenamento local do navegador para manter a sessão autenticada
          (token JWT). Esses dados são necessários para o funcionamento da plataforma e não são
          utilizados para fins de publicidade ou rastreamento de terceiros.
        </p>
      </LegalSection>

      <LegalSection title="11. Alterações nesta política">
        <p>
          Podemos atualizar esta Política de Privacidade periodicamente. A data da última
          atualização será indicada no topo desta página. Recomendamos que você revise este
          documento regularmente.
        </p>
      </LegalSection>

      <LegalSection title="12. Contato">
        <p>
          Em caso de dúvidas sobre esta política ou sobre o tratamento dos seus dados pessoais,
          entre em contato:
        </p>
        <p>
          <strong>{COMPANY_NAME}</strong>
          <br />
          CNPJ: {COMPANY_CNPJ}
          <br />
          E-mail:{' '}
          <a href={`mailto:${COMPANY_EMAIL}`} className="text-forge-600 hover:underline">
            {COMPANY_EMAIL}
          </a>
        </p>
      </LegalSection>
    </LegalPageLayout>
  );
}

export default PrivacyPolicy;
